import { useEffect, useState } from 'react';
import { fetchIntegration, type IntegrationRecord } from '../../api/dataApi';
import { relativeAgo } from '../../lib/integrationHealth';

const RUNTIME_COPY: Record<string, string> = {
  cloud_webhook: 'Zmist receives it: your system POSTs to the webhook URL.',
  remote_write: 'Prometheus pushes to Zmist (Remote Write).',
  agent_pull: 'An agent polls it from a host that can reach the source.',
  agent_file: 'An agent reads a local file or piped stream on its host.',
  agent_host: "An agent reports its own machine's CPU, memory, disks, network and sensors.",
  cloud_poll: 'Zmist itself polls it on a schedule - no agent needed.',
};

// Where an integration runs, from the server-side Integration read-model
// (GET /integrations). For an agent-executed source it lists the agents that
// have actually run it, whether each is online, and configuration drift
// ("running v3, published v4"). Hidden when the read fails (an older backend
// without the endpoint) - this card adds information, it never gates anything.
export function RuntimeCard({ nickname }: { nickname: string }) {
  const [rec, setRec] = useState<IntegrationRecord | null>(null);

  useEffect(() => {
    let alive = true;
    fetchIntegration(nickname)
      .then((r) => alive && setRec(r))
      .catch(() => alive && setRec(null));
    return () => {
      alive = false;
    };
  }, [nickname]);

  if (!rec || !rec.runtime) return null;
  const a = rec.assignment;
  return (
    <div className="flex flex-col gap-1.5 text-sm" data-testid="runtime-card">
      <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">Runs on</span>
      <span>{RUNTIME_COPY[rec.runtime] ?? rec.runtime}</span>
      {a && (
        <>
          {a.pinnedAgent && (
            <span className="text-xs text-black/55 dark:text-white/55">
              Pinned to agent <span className="font-mono">{a.pinnedAgent}</span> - other agents skip it.
            </span>
          )}
          {a.agents.length === 0 ? (
            <span className="text-xs text-black/45 dark:text-white/45">
              No agent has run it yet. Run <code className="font-mono">cue-cli import --mapping {rec.nickname}</code> on the host that can reach the source.
            </span>
          ) : (
            <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
              {a.agents.map((ag) => (
                <li key={ag.name} className="flex items-center gap-2 text-xs">
                  <span className={`h-1.5 w-1.5 rounded-full ${ag.online ? 'bg-emerald-500' : 'bg-amber-500'}`} aria-hidden />
                  <span className="font-mono">{ag.name}</span>
                  <span className="text-black/40 dark:text-white/40">{ag.online ? 'online' : 'offline'} · last ran {relativeAgo(ag.lastSeenAt)}</span>
                  {ag.drift && (
                    <span className="text-amber-600 dark:text-amber-400">
                      running v{ag.runningVersion}, published v{rec.mapping.activeVersion}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
